/* AlertSnackbar.js */

import React, { useEffect, useRef, useState } from "react";
import { useSnackbar } from "./SnackbarContext";
import "./AlertSnackbar.css";

export default function AlertSnackbar() {
  const { snackbar, closeSnackbar } = useSnackbar() || {};
  const open = !!snackbar?.open;

  const [toastRendered, setToastRendered] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);

  // Forces backdrop animation to restart on each open
  const [backdropKey, setBackdropKey] = useState(0);

  const autoCloseRef = useRef(null);

  const close = () => closeSnackbar?.();

  // Backdrop: restart the one-shot CSS animation each time open becomes true
  useEffect(() => {
    if (open) {
      setBackdropKey((k) => k + 1);
    }
  }, [open]);

  // Toast: mount/unmount without any duration constants (use transitionend)
  useEffect(() => {
    if (open) {
      setToastRendered(true);

      // ensure initial "hidden" paint before showing (reliable transition)
      requestAnimationFrame(() => setToastVisible(true));

      if (!snackbar?.persist) {
        if (autoCloseRef.current) clearTimeout(autoCloseRef.current);
        autoCloseRef.current = setTimeout(close, snackbar?.duration ?? 5000);
      }
    } else {
      setToastVisible(false);
    }

    return () => {
      if (autoCloseRef.current) clearTimeout(autoCloseRef.current);
      autoCloseRef.current = null;
    };
  }, [open]);

  const onToastTransitionEnd = (e) => {
    // Only act on the card's own opacity transition ending — transform ends
    // separately too, and would otherwise unmount the card while it's still moving.
    if (e.target !== e.currentTarget) return;
    if (e.propertyName !== "opacity") return;
    if (!toastVisible) setToastRendered(false);
  };

  const title = snackbar?.title;
  const icon = snackbar?.icon;
  const actions = Array.isArray(snackbar?.actions) ? snackbar.actions : [];

  return (
    <>
      {toastRendered && (
        <div key={backdropKey} className="toast-backdrop animate" />
      )}

      {toastRendered && (
        <div className={`toast-host ${toastVisible ? "show" : ""}`}>
          <div className="toast-card" onTransitionEnd={onToastTransitionEnd}>
            <div className="toast-edge" />
            <div className="toast-nebula" />
            <div className="toast-sheen" />

            <div className="toast-inner">
              <div className="toast-row">
                {(icon || title) ? (
                  <div className="toast-icon">{icon ?? "⚡"}</div>
                ) : null}

                <div className="toast-content">
                  {title ? <div className="toast-title">{title}</div> : null}
                  <div className="toast-message">{snackbar?.message}</div>
                </div>

                <button
                  type="button"
                  className="toast-close"
                  onClick={close}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              {actions.length > 0 ? (
                <div className="toast-actions">
                  {actions.map((a, idx) => (
                    <button
                      key={`${a?.label ?? "action"}-${idx}`}
                      type="button"
                      className={`toast-btn ${
                        a?.variant === "ghost" ? "ghost" : "primary"
                      }`}
                      onClick={() => {
                        a?.onClick?.();
                        if (!a?.keepOpen) close();
                      }}
                    >
                      {a?.label ?? "Action"}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </>
  );
}